import React, { useState } from 'react'
import type { Assignment, CreateAssignmentRequest } from '@/types/assignment'

interface AssignmentFormModalProps {
  lessonId: string
  assignment?: Assignment
  onClose: () => void
  onSave: (data: CreateAssignmentRequest) => Promise<void>
  isSaving?: boolean
}

export const AssignmentFormModal: React.FC<AssignmentFormModalProps> = ({ lessonId, assignment, onClose, onSave, isSaving: externalSaving }) => {
  const [formData, setFormData] = useState<Partial<CreateAssignmentRequest>>(
    assignment ? {
      title: assignment.title,
      description: assignment.description,
      instructions: assignment.instructions,
      maxScore: assignment.maxScore,
      passingScore: assignment.passingScore,
      dueDateDays: assignment.dueDateDays,
      allowLateSubmission: assignment.allowLateSubmission,
      fileTypesAllowed: assignment.fileTypesAllowed,
      maxFileSizeMB: assignment.maxFileSizeMB,
      lessonId: lessonId,
    } : {
      title: '',
      description: '',
      instructions: '',
      maxScore: 100,
      passingScore: 70,
      dueDateDays: 7,
      allowLateSubmission: true,
      fileTypesAllowed: ['pdf', 'doc', 'docx', 'zip'],
      maxFileSizeMB: 10,
      lessonId: lessonId,
    }
  )

  const [isSaving, setIsSaving] = useState(false)
  const [errors, setErrors] = useState<Record<string, string>>({})

  const isEditMode = !!assignment

  const validate = () => {
    const newErrors: Record<string, string> = {}
    if (!formData.title?.trim()) newErrors.title = 'Judul wajib diisi'
    if ((formData.maxScore || 0) < 1) newErrors.maxScore = 'Skor minimal 1'
    if ((formData.passingScore || 0) < 0 || (formData.passingScore || 0) > 100) {
      newErrors.passingScore = 'Passing score harus 0-100'
    }
    if ((formData.maxFileSizeMB || 0) < 1) newErrors.maxFileSizeMB = 'Ukuran file minimal 1MB'

    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!validate()) return

    setIsSaving(true)
    try {
      await onSave({ ...formData, lessonId } as CreateAssignmentRequest)
    } catch (error) {
      console.error('Failed to save assignment:', error)
    } finally {
      setIsSaving(false)
    }
  }

  const saving = externalSaving || isSaving

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 sm:p-6">
      {/* Backdrop */}
      <div 
        className="absolute inset-0 bg-slate-900/60 backdrop-blur-md transition-opacity" 
        onClick={onClose}
      />
      
      {/* Modal Content */}
      <div className="relative w-full max-w-2xl bg-white dark:bg-slate-900 rounded-[2.5rem] shadow-[0_32px_64px_-12px_rgba(0,0,0,0.2)] border border-slate-100 dark:border-white/5 overflow-hidden flex flex-col max-h-[90vh]">
        
        {/* Header */}
        <div className="flex-shrink-0 flex items-center justify-between p-6 border-b border-slate-100 dark:border-white/5 bg-white/50 dark:bg-white/[0.02] backdrop-blur-xl">
          <div className="flex items-center gap-3">
            <div className="size-10 rounded-xl bg-primary/10 flex items-center justify-center text-primary">
              <span className="material-symbols-outlined">{isEditMode ? 'assignment' : 'post_add'}</span>
            </div>
            <div>
              <h2 className="text-sm font-black text-slate-900 dark:text-white tracking-tight">
                {isEditMode ? 'Edit Tugas' : 'Buat Tugas Baru'}
              </h2>
              <p className="text-xs font-bold text-slate-400 mt-0.5">Berikan tugas praktek untuk peserta</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="size-10 rounded-xl flex items-center justify-center hover:bg-slate-100 dark:hover:bg-white/5 text-slate-400 hover:text-slate-900 dark:hover:text-white transition-all"
          >
            <span className="material-symbols-outlined">close</span>
          </button>
        </div>

        {/* Scrollable Body */}
        <div className="flex-1 overflow-y-auto custom-scrollbar">
          <form id="assignment-form" onSubmit={handleSubmit} className="p-8 space-y-8">
            {/* Title */}
            <div className="space-y-1">
              <label className="block text-xs font-black text-slate-500 dark:text-slate-400 ml-1">
                Judul Tugas <span className="text-rose-500">*</span>
              </label>
              <input
                type="text"
                value={formData.title}
                onChange={(e) => {
                  setFormData((prev) => ({ ...prev, title: e.target.value }))
                  setErrors((prev) => ({ ...prev, title: '' }))
                }}
                className={`w-full px-5 py-3.5 rounded-2xl bg-slate-50 dark:bg-white/[0.03] border text-sm font-bold text-slate-900 dark:text-white placeholder-slate-400 transition-all outline-none focus:ring-4 focus:ring-primary/10 ${
                  errors.title ? 'border-rose-500' : 'border-slate-200 dark:border-white/5 focus:border-primary'
                }`}
                placeholder="Contoh: Tugas Mandiri Modul 1"
              />
              {errors.title && <p className="mt-2 text-xs font-bold text-rose-500 ml-1">{errors.title}</p>}
            </div>

            {/* Description */}
            <div className="space-y-1">
              <label className="block text-xs font-black text-slate-500 dark:text-slate-400 ml-1">Deskripsi Singkat</label>
              <textarea
                value={formData.description}
                onChange={(e) => setFormData((prev) => ({ ...prev, description: e.target.value }))}
                rows={2}
                className="w-full px-5 py-3.5 rounded-2xl bg-slate-50 dark:bg-white/[0.03] border border-slate-200 dark:border-white/5 text-sm font-bold text-slate-900 dark:text-white placeholder-slate-400 transition-all outline-none focus:ring-4 focus:ring-primary/10 focus:border-primary resize-none"
                placeholder="Penjelasan singkat mengenai tugas ini..."
              />
            </div>

            {/* Instructions */}
            <div className="space-y-1">
              <label className="block text-xs font-black text-slate-500 dark:text-slate-400 ml-1">Instruksi Lengkap</label>
              <textarea
                value={formData.instructions}
                onChange={(e) => setFormData((prev) => ({ ...prev, instructions: e.target.value }))}
                rows={4}
                className="w-full px-5 py-3.5 rounded-2xl bg-slate-50 dark:bg-white/[0.03] border border-slate-200 dark:border-white/5 text-sm font-bold text-slate-900 dark:text-white placeholder-slate-400 transition-all outline-none focus:ring-4 focus:ring-primary/10 focus:border-primary resize-none"
                placeholder="Tuliskan instruksi pengerjaan tugas secara detail di sini..."
              />
            </div>

            {/* Scoring Grid */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
              <div className="space-y-1">
                <label className="block text-xs font-black text-slate-500 dark:text-slate-400 ml-1">
                  Skor Maksimum <span className="text-rose-500">*</span>
                </label>
                <input
                  type="number"
                  value={formData.maxScore}
                  onChange={(e) => {
                    setFormData((prev) => ({ ...prev, maxScore: Number(e.target.value) }))
                    setErrors((prev) => ({ ...prev, maxScore: '' }))
                  }}
                  min="1"
                  className={`w-full px-5 py-3.5 rounded-2xl bg-slate-50 dark:bg-white/[0.03] border text-sm font-bold text-slate-900 dark:text-white transition-all outline-none focus:ring-4 focus:ring-primary/10 ${
                    errors.maxScore ? 'border-rose-500' : 'border-slate-200 dark:border-white/5 focus:border-primary'
                  }`}
                />
                {errors.maxScore && <p className="mt-2 text-xs font-bold text-rose-500 ml-1">{errors.maxScore}</p>}
              </div>

              <div className="space-y-1">
                <label className="block text-xs font-black text-slate-500 dark:text-slate-400 ml-1">
                  Passing Score (%) <span className="text-rose-500">*</span>
                </label>
                <div className="relative">
                  <input
                    type="number"
                    value={formData.passingScore}
                    onChange={(e) => {
                      setFormData((prev) => ({ ...prev, passingScore: Number(e.target.value) }))
                      setErrors((prev) => ({ ...prev, passingScore: '' }))
                    }}
                    min="0"
                    max="100"
                    className={`w-full pl-5 pr-12 py-3.5 rounded-2xl bg-slate-50 dark:bg-white/[0.03] border text-sm font-bold text-slate-900 dark:text-white transition-all outline-none focus:ring-4 focus:ring-primary/10 ${
                      errors.passingScore ? 'border-rose-500' : 'border-slate-200 dark:border-white/5 focus:border-primary'
                    }`}
                  />
                  <span className="absolute right-5 top-1/2 -translate-y-1/2 text-xs font-black text-slate-400">%</span>
                </div>
                {errors.passingScore && <p className="mt-2 text-xs font-bold text-rose-500 ml-1">{errors.passingScore}</p>}
              </div>
            </div>

            {/* Deadline Grid */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 p-6 rounded-3xl bg-slate-50 dark:bg-white/[0.02] border border-slate-100 dark:border-white/5">
              <div className="space-y-1">
                <label className="block text-xs font-black text-slate-500 dark:text-slate-400 ml-1">
                  Batas Waktu (Hari)
                </label>
                <div className="relative">
                  <input
                    type="number"
                    value={formData.dueDateDays}
                    onChange={(e) => setFormData((prev) => ({ ...prev, dueDateDays: Number(e.target.value) }))}
                    min="1"
                    className="w-full pl-5 pr-12 py-3.5 rounded-2xl bg-white dark:bg-white/[0.03] border border-slate-200 dark:border-white/5 text-sm font-bold text-slate-900 dark:text-white transition-all outline-none focus:ring-4 focus:ring-primary/10 focus:border-primary"
                    placeholder="Contoh: 7"
                  />
                  <span className="absolute right-5 top-1/2 -translate-y-1/2 text-[10px] font-black text-slate-400">hari</span>
                </div>
                <p className="mt-2 text-[10px] text-slate-400 font-bold ml-1">Setelah siswa enroll</p>
              </div>

              <div className="flex items-center pt-6 sm:pt-0">
                <div 
                  onClick={() => setFormData((prev) => ({ ...prev, allowLateSubmission: !prev.allowLateSubmission }))}
                  className="flex items-center justify-between w-full cursor-pointer group bg-white dark:bg-white/[0.03] p-4 rounded-2xl border border-slate-200 dark:border-white/5 hover:border-primary/30 transition-all"
                >
                  <span className="text-xs font-black text-slate-700 dark:text-slate-200">Izinkan Terlambat</span>
                  <div className={`relative w-12 h-6 rounded-full transition-all duration-300 ${formData.allowLateSubmission ? 'bg-primary' : 'bg-slate-200 dark:bg-white/10'}`}>
                    <div className={`absolute top-1 left-1 size-4 rounded-full bg-white shadow-md transition-transform duration-300 ${formData.allowLateSubmission ? 'translate-x-6' : 'translate-x-0'}`} />
                  </div>
                </div>
              </div>
            </div>

            {/* File Settings Grid */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
              <div className="space-y-1">
                <label className="block text-xs font-black text-slate-500 dark:text-slate-400 ml-1">
                  Format File (Ekstensi)
                </label>
                <input
                  type="text"
                  value={Array.isArray(formData.fileTypesAllowed) ? formData.fileTypesAllowed.join(', ') : formData.fileTypesAllowed}
                  onChange={(e) => setFormData((prev) => ({ ...prev, fileTypesAllowed: e.target.value.split(',').map(s => s.trim()).filter(Boolean) }))}
                  className="w-full px-5 py-3.5 rounded-2xl bg-slate-50 dark:bg-white/[0.03] border border-slate-200 dark:border-white/5 text-sm font-bold text-slate-900 dark:text-white transition-all outline-none focus:ring-4 focus:ring-primary/10 focus:border-primary"
                  placeholder="pdf, doc, docx, zip"
                />
                <p className="mt-2 text-[10px] text-slate-400 font-bold ml-1">Pisahkan dengan koma</p>
              </div>

              <div className="space-y-1">
                <label className="block text-xs font-black text-slate-500 dark:text-slate-400 ml-1">
                  Ukuran Maks (MB) <span className="text-rose-500">*</span>
                </label>
                <div className="relative">
                  <input
                    type="number"
                    value={formData.maxFileSizeMB}
                    onChange={(e) => {
                      setFormData((prev) => ({ ...prev, maxFileSizeMB: Number(e.target.value) }))
                      setErrors((prev) => ({ ...prev, maxFileSizeMB: '' }))
                    }}
                    min="1"
                    className={`w-full pl-5 pr-12 py-3.5 rounded-2xl bg-slate-50 dark:bg-white/[0.03] border text-sm font-bold text-slate-900 dark:text-white transition-all outline-none focus:ring-4 focus:ring-primary/10 ${
                      errors.maxFileSizeMB ? 'border-rose-500' : 'border-slate-200 dark:border-white/5 focus:border-primary'
                    }`}
                  />
                  <span className="absolute right-5 top-1/2 -translate-y-1/2 text-xs font-black text-slate-400">MB</span>
                </div>
                {errors.maxFileSizeMB && <p className="mt-2 text-xs font-bold text-rose-500 ml-1">{errors.maxFileSizeMB}</p>}
              </div>
            </div>
          </form>
        </div>

        {/* Footer Actions */}
        <div className="flex-shrink-0 flex items-center justify-end gap-3 p-6 border-t border-slate-100 dark:border-white/5 bg-white/50 dark:bg-white/[0.02] backdrop-blur-xl">
          <button
            type="button"
            onClick={onClose}
            disabled={saving}
            className="px-6 py-3 rounded-2xl text-xs font-black text-slate-500 dark:text-slate-400 hover:text-slate-900 dark:hover:text-white hover:bg-slate-100 dark:hover:bg-white/5 transition-all disabled:opacity-50"
          >
            Batal
          </button>
          <button
            form="assignment-form"
            type="submit"
            disabled={saving}
            className="px-8 py-3 bg-primary text-white rounded-2xl font-black text-xs hover:opacity-90 active:scale-95 transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2 shadow-xl shadow-primary/20"
          >
            {saving ? (
              <>
                <div className="animate-spin rounded-full h-3 w-3 border-2 border-white/30 border-t-white" />
                <span>Menyimpan...</span>
              </>
            ) : (
              <>
                <span className="material-symbols-outlined text-[18px]">{isEditMode ? 'check_circle' : 'add_circle'}</span>
                <span>{isEditMode ? 'Update Tugas' : 'Simpan Tugas'}</span>
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  )
}

